/**
 * One-click zone templates — pre-fill 2-3 zones for the most common big-country
 * setups, so admins don't have to learn the ISO 3166-2 universe to ship to India.
 *
 * Each template returns an array of fully-formed ShippingZone objects. The
 * admin can save them as-is or tweak rates from there.
 *
 * `priority` is assigned contiguously per template; the editor will rebase
 * priorities globally on save based on array position, so these values only
 * matter for the relative order WITHIN the template.
 */

import type { ShippingZone } from '@/types';

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface ZoneTemplate {
  id: string;
  label: string;
  description: string;
  build: () => ShippingZone[];
}

export const ZONE_TEMPLATES: ZoneTemplate[] = [
  {
    id: 'blank',
    label: 'Blank zone',
    description: 'Start from scratch with a single empty zone.',
    build: () => [
      {
        id: newId(),
        name: 'New zone',
        priority: 0,
        enabled: true,
        match: { countries: [] },
        rate: 0,
        estimatedDays: '3-5 business days',
      },
    ],
  },

  {
    id: 'in-3zone',
    label: 'India: Metro / North-East / Rest',
    description:
      'Three zones: metro cities (cheapest), North-East states (most expensive), rest of India (standard).',
    build: () => [
      {
        id: newId(),
        name: 'India — Metro cities',
        priority: 0,
        enabled: true,
        match: {
          countries: ['IN'],
          regions: ['DL', 'MH', 'KA', 'TN', 'TG', 'WB', 'GJ'],
        },
        rate: 4900, // ₹49
        freeAbove: 99900, // ₹999
        estimatedDays: '1-2 days',
      },
      {
        id: newId(),
        name: 'India — North-East',
        priority: 1,
        enabled: true,
        match: {
          countries: ['IN'],
          regions: ['AR', 'AS', 'MN', 'ML', 'MZ', 'NL', 'SK', 'TR'],
        },
        rate: 14900, // ₹149
        freeAbove: 199900, // ₹1,999
        estimatedDays: '5-7 days',
      },
      {
        id: newId(),
        name: 'India — Rest of India',
        priority: 2,
        enabled: true,
        match: { countries: ['IN'] },
        rate: 7900, // ₹79
        freeAbove: 149900, // ₹1,499
        estimatedDays: '3-5 days',
      },
    ],
  },

  {
    id: 'ca-2zone',
    label: 'Canada: Mainland / Territories',
    description:
      'Two zones: mainland provinces (cheaper) and northern territories (remote shipping surcharge).',
    build: () => [
      {
        id: newId(),
        name: 'Canada — Territories',
        priority: 0,
        enabled: true,
        match: { countries: ['CA'], regions: ['NT', 'NU', 'YT'] },
        rate: 2500, // CA$25
        estimatedDays: '7-10 business days',
      },
      {
        id: newId(),
        name: 'Canada — Mainland',
        priority: 1,
        enabled: true,
        match: { countries: ['CA'] },
        rate: 1000, // CA$10
        freeAbove: 15000, // CA$150
        estimatedDays: '3-5 business days',
      },
    ],
  },

  {
    id: 'us-3zone',
    label: 'US: Mainland / AK & HI / Territories',
    description:
      'Three zones: mainland (default), Alaska + Hawaii (surcharge), US territories (highest).',
    build: () => [
      {
        id: newId(),
        name: 'US — Territories',
        priority: 0,
        enabled: true,
        match: { countries: ['US'], regions: ['AS', 'GU', 'MP', 'PR', 'VI'] },
        rate: 3000, // $30
        estimatedDays: '7-14 business days',
      },
      {
        id: newId(),
        name: 'US — Alaska & Hawaii',
        priority: 1,
        enabled: true,
        match: { countries: ['US'], regions: ['AK', 'HI'] },
        rate: 1800, // $18
        estimatedDays: '5-7 business days',
      },
      {
        id: newId(),
        name: 'US — Mainland',
        priority: 2,
        enabled: true,
        match: { countries: ['US'] },
        rate: 700, // $7
        freeAbove: 5000, // $50
        estimatedDays: '3-5 business days',
      },
    ],
  },

  {
    id: 'eu-single',
    label: 'EU: single zone',
    description: 'One zone covering the European Union core countries at a flat rate.',
    build: () => [
      {
        id: newId(),
        name: 'European Union',
        priority: 0,
        enabled: true,
        match: {
          countries: ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'SE', 'DK', 'FI', 'PL'],
        },
        rate: 990, // €9.90
        freeAbove: 9900, // €99
        estimatedDays: '4-7 business days',
      },
    ],
  },

  {
    id: 'worldwide',
    label: 'International — everywhere else',
    description: 'Catch-all zone with a single high priority number so specific zones match first.',
    build: () => [
      {
        id: newId(),
        name: 'International',
        priority: 99,
        enabled: true,
        // Empty countries array isn't valid — admins edit to add the countries
        // they actually ship to. We seed with common ones.
        match: {
          countries: ['GB', 'AU', 'NZ', 'SG', 'AE', 'JP', 'KR'],
        },
        rate: 2500,
        estimatedDays: '7-14 business days',
      },
    ],
  },
];
